import { useEffect } from "react";
import NextImage from "next/image";
import { Box, Stack, lighten, useTheme, Typography } from "@mui/material";

import PartyIcon from "public/images/icon_party.svg";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";

import {
  findSpecialEvents,
  isSpecialEventSelected,
  findSmallestMinCountSpecialEvent,
} from "../utils.events";
import { useAppStore } from "@/global/store";
import { usePacks } from "../Packs/usePacks";
import {
  selectEvents,
  selectEventsSelected,
  selectIsEventsLoading,
  selectRegularEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { useBuckets } from "../Buckets/useBuckets";
import { useMinEventCount } from "../useMinEventCount";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { pluralize, formatDate } from "@/global/utils/utils";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useDynamicPricing } from "../DynamicPricing/useDynamicPricing";
import { useSPEventSelectorModal } from "../modals/SPEventSelectorModal";
import { useSPEventRemoveConfirmModal } from "../modals/SPEventRemoveConfirmModal";

export function GameSelectionProgress() {
  const allEvents = useAppStore(selectEvents);
  const specialEvents = findSpecialEvents(allEvents);
  const isEventsLoading = useAppStore(selectIsEventsLoading);
  const { isBucketsEnabled } = useBuckets();
  const { isPacksEnabled } = usePacks();

  if (isEventsLoading) return <GameSelectionProgressSkeleton />;
  if (specialEvents.length > 0) return <SPEventProgress />;
  if (isBucketsEnabled) return <BucketsProgressBar />;
  if (isPacksEnabled) return <PackBucketsProgressBar />;

  return <RegularProgressBar />;
}

export function LinearProgressBar(props: {
  progress: number; // percentage
}) {
  const theme = useTheme();

  return (
    <Box
      bgcolor={lighten(theme.palette.primary.main, 0.7)}
      width="100%"
      height="10px"
      borderRadius={"6px"}
      overflow={"hidden"}
    >
      <Box
        bgcolor={theme.palette.primary.main}
        height="100%"
        width={`${props.progress}%`}
        sx={{ transition: "all 0.2s ease" }}
      />
    </Box>
  );
}

function SuccessMessage(props: { imageSrc: string; text: string }) {
  const { sm: smDown } = useCssMediaQuery("down");
  return (
    <Stack direction={"row"} spacing={smDown ? 1 : 2} alignItems={"center"}>
      <NextImage
        src={props?.imageSrc}
        alt="Party Icon"
        width={smDown ? 24 : 28}
        height={smDown ? 24 : 28}
      />
      <Box>
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight={"500"}
          fontSize={smDown ? "12px" : "14px"}
        >
          {props.text}
        </Typography>
      </Box>
    </Stack>
  );
}

function GameSelectionProgressSkeleton() {
  const { sm: smDown } = useCssMediaQuery("down");
  return (
    <Stack direction={"row"} spacing={1.5} width={smDown ? "100%" : "400px"}>
      <Box>
        <DynamicSkeleton
          rowCount={1}
          widths={["100%"]}
          heights={["100%"]}
          spacing={0}
          style={{
            container: { padding: "8px", width: "48px", height: "48px" },
            gridContainer: { height: "100% !important" },
            item: { borderRadius: "4px", height: "100% !important" },
          }}
        />
      </Box>

      <Box flexGrow={1}>
        <DynamicSkeleton
          rowCount={2}
          widths={["100%", "70%"]}
          heights={["13px", "13px"]}
          spacing={1}
          style={{
            container: { padding: "8px" },
            item: { borderRadius: "4px" },
          }}
        />
      </Box>
    </Stack>
  );
}

export function RegularProgressBar() {
  const theme = useTheme();
  const { sm: smDown } = useCssMediaQuery("down");
  const { teamConfig } = useAppSystem();
  const { languages, uiConfigs } = teamConfig;
  const { minGameSelectionCount } = useMinEventCount();
  const { selectedPricing } = useDynamicPricing();
  const selectedEvents = useAppStore(selectEventsSelected);
  const remaningGameCount = minGameSelectionCount - selectedEvents.length;
  const isStaticProgressBarText = uiConfigs?.progressBar?.staticProgressBarText || false; // prettier-ignore
  const progressBarSuccessText = selectedPricing?.customProgressBarMessage || languages?.purchaseSeats?.progressBarSuccessText || "You're good to go! Add more games or checkout now!"; // prettier-ignore
  const progressBarText = languages?.purchaseSeats?.progressBarText ?? null;
  const progressBarAditionalText = languages?.purchaseSeats?.progressBarAditionalText ?? null; // prettier-ignore
  const progressBarSuccessAditionalText = languages?.purchaseSeats?.progressBarSuccessAditionalText ?? null; // prettier-ignore
  const progressBarGameText = languages?.purchaseSeats?.progressBarGameText || "game"; // prettier-ignore
  const customProgressBarSize = uiConfigs?.progressBar?.customProgressBarSize;

  return (
    <Box
      p={smDown ? 1 : 2}
      borderRadius={"8px"}
      border={`1px solid ${lighten(theme.palette.primary.main, 0.6)}`}
      bgcolor={lighten(theme.palette.primary.main, 0.95)}
      width={smDown ? "100%" : customProgressBarSize || "400px"}
    >
      {/* Progress bar */}
      {remaningGameCount > 0 ? (
        <Box width="100%">
          <Box>
            <LinearProgressBar
              progress={(selectedEvents.length / minGameSelectionCount) * 100}
            />
          </Box>

          {/* progress message */}
          <Box mt={1}>
            <Typography
              variant="body2"
              color="text.primary"
              fontWeight={"500"}
              fontSize={smDown ? "12px" : "14px"}
            >
              {
                progressBarText && (isStaticProgressBarText || remaningGameCount == minGameSelectionCount) ? progressBarText : remaningGameCount == minGameSelectionCount
                ? `Add ${minGameSelectionCount} ${pluralize(progressBarGameText, minGameSelectionCount)} ${progressBarAditionalText ? progressBarAditionalText : "to continue!"}` // prettier-ignore
                : `Add ${remaningGameCount} more ${pluralize(progressBarGameText, remaningGameCount)}${progressBarAditionalText ? ` ${progressBarAditionalText}` : ''}` // prettier-ignore
              }
            </Typography>
          </Box>
        </Box>
      ) : null}

      {/* success language */}
      {remaningGameCount <= 0 ? (
        <SuccessMessage imageSrc={PartyIcon.src} text={`${progressBarSuccessText} ${progressBarSuccessAditionalText ? progressBarSuccessAditionalText : ''}`} /> // prettier-ignore
      ) : null}
    </Box>
  );
}

export function SPEventProgress() {
  // configs
  const theme = useTheme();
  const { sm: smDown } = useCssMediaQuery("down");
  const { minGameSelectionCount } = useMinEventCount();
  const { teamConfig: { languages } } = useAppSystem(); // prettier-ignore

  // modals
  const spEventSelectorModal = useSPEventSelectorModal();
  const spEventRemoveConfirmModal = useSPEventRemoveConfirmModal();

  // states
  const allEvents = useAppStore(selectEvents);
  const allSpecialEvents = findSpecialEvents(allEvents);
  const selectedEvents = useAppStore(selectEventsSelected);
  const selectedRegularEvents = useAppStore(selectRegularEventsSelected);
  const unlockableSpecialEvent = findSmallestMinCountSpecialEvent(allSpecialEvents); // prettier-ignore
  const isSpecialEvtSelected = isSpecialEventSelected(selectedEvents);

  const selectedRegularEventLen = selectedRegularEvents.length;
  const unlockableSPEventMinCount = unlockableSpecialEvent?.minEventSelectionRequired || 0; // prettier-ignore
  const progress = (selectedRegularEventLen / unlockableSPEventMinCount) * 100; // prettier-ignore
  const remainingSPCount = unlockableSPEventMinCount - selectedRegularEventLen;
  const remainingRegCount = minGameSelectionCount - selectedRegularEventLen;

  const progressBarSpecialEventsCustomText = languages?.purchaseSeats?.progressBarSpecialEventsCustomText; // prettier-ignore

  useEffect(() => {
    if (!unlockableSpecialEvent) return;

    const shouldOpenSPModal =
      !isSpecialEvtSelected &&
      !spEventSelectorModal.isOpened &&
      selectedRegularEventLen >= unlockableSPEventMinCount;

    if (shouldOpenSPModal) {
      spEventSelectorModal.set({
        isOpen: true,
        isOpened: true,
        eventsToSelect: allSpecialEvents,
      });
    }

    const shouldOpenRemoveModal =
      isSpecialEvtSelected &&
      selectedRegularEventLen < unlockableSPEventMinCount;

    if (shouldOpenRemoveModal) {
      spEventRemoveConfirmModal.set({
        isOpen: true,
        eventsToRemove: [unlockableSpecialEvent],
      });
    }

    if (selectedRegularEventLen < unlockableSPEventMinCount) {
      spEventSelectorModal.set({ isOpened: false });
    }
  }, [selectedEvents]);

  const specialEventsProgressBarWord = languages?.purchaseSeats?.specialEventsProgressBarWord || "Match"; // prettier-ignore
  const specialEventDate = formatDate(unlockableSpecialEvent?.date, false, true); // prettier-ignore
  const specialEventTitle = languages?.purchaseSeats?.customSpecialEventText || (<b>{unlockableSpecialEvent?.title.replace("vs. ", "")}</b>); // prettier-ignore
  const progressText = <>{`Add ${remainingSPCount} more ${pluralize("game", remainingSPCount)} to unlock the `}{specialEventTitle}{` ${specialEventsProgressBarWord} on `}{specialEventDate}</>; // prettier-ignore
  const customProgressText = (
    <>{`Add ${remainingSPCount} more ${pluralize(
      "game",
      remainingRegCount
    )} ${progressBarSpecialEventsCustomText}`}</>
  );
  const progressBarSuccessText = languages?.purchaseSeats?.progressBarSuccessText || "You're good to go! Add more games or checkout now!"; // prettier-ignore

  return (
    <Box
      p={smDown ? 1 : 2}
      borderRadius={"8px"}
      border={`1px solid ${lighten(theme.palette.primary.main, 0.6)}`}
      bgcolor={lighten(theme.palette.primary.main, 0.95)}
      width={smDown ? "100%" : "400px"}
    >
      {/* Progress bar */}
      {remainingSPCount > 0 ? (
        <Box width="100%">
          <LinearProgressBar progress={progress} />

          <Box mt={1}>
            <Typography
              variant="body2"
              color="text.primary"
              fontWeight={"500"}
              fontSize={smDown ? "12px" : "14px"}
            >
              {progressBarSpecialEventsCustomText
                ? customProgressText
                : progressText}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {/* success languages */}
      {remainingSPCount <= 0 && remainingRegCount <= 0 ? (
        <SuccessMessage
          imageSrc={PartyIcon.src}
          text={progressBarSuccessText}
        />
      ) : null}

      {remainingSPCount <= 0 && remainingRegCount > 0 ? (
        <SuccessMessage
          imageSrc={PartyIcon.src}
          text={`Add ${remainingRegCount} more ${pluralize(
            "game",
            remainingRegCount
          )}`}
        />
      ) : null}
    </Box>
  );
}

export function BucketsProgressBar() {
  const theme = useTheme();
  const { teamConfig } = useAppSystem();
  const { languages, purchaseSeatConfigs } = teamConfig;
  const { sm: smDown } = useCssMediaQuery("down");
  const {
    bucketMin,
    bucketMessage,
    bucketsRemainingCount,
    selectedEventsPerBucket,
    prioritizeMinGameSelectionCount,
  } = useBuckets();
  const { selectedPricing } = useDynamicPricing();

  const selectedEvents = useAppStore(selectEventsSelected);
  const progressBarLanguage =
    selectedPricing?.customProgressBarMessage ||
    languages?.purchaseSeats?.progressBarSuccessText ||
    "You're good to go! Add more games or checkout now!";

  const minGameSelectionCount = purchaseSeatConfigs.minGameSelectionCount;
  const remaningGameCount = minGameSelectionCount - selectedEvents.length;
  const isProgressNotReached =
    (selectedEventsPerBucket / bucketMin) * 100 != 100;

  return (
    <Box
      p={smDown ? 1 : 2}
      borderRadius={"8px"}
      border={`1px solid ${lighten(theme.palette.primary.main, 0.6)}`}
      bgcolor={lighten(theme.palette.primary.main, 0.95)}
      width={smDown ? "100%" : "400px"}
    >
      {/* Progress bar */}
      {remaningGameCount > 0 && prioritizeMinGameSelectionCount ? (
        <Box width="100%">
          <Box>
            <LinearProgressBar
              progress={(selectedEvents.length / minGameSelectionCount) * 100}
            />
          </Box>

          {/* progress message */}
          {bucketMessage ? (
            <Box
              mt={1}
              maxWidth={"100%"}
              sx={{
                [theme.breakpoints.down(421)]: {
                  maxWidth: 160,
                },
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight={"500"}
                fontSize={smDown ? "12px" : "14px"}
              >
                {bucketMessage}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {isProgressNotReached && !prioritizeMinGameSelectionCount ? (
        <Box width="100%">
          <Box>
            <LinearProgressBar
              progress={(selectedEventsPerBucket / bucketMin) * 100}
            />
          </Box>

          {/* progress message */}
          {bucketMessage ? (
            <Box
              mt={1}
              maxWidth={"100%"}
              sx={{
                [theme.breakpoints.down(421)]: {
                  maxWidth: 160,
                },
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight={"500"}
                fontSize={smDown ? "12px" : "14px"}
              >
                {bucketMessage}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* success language */}
      {remaningGameCount <= 0 && prioritizeMinGameSelectionCount ? (
        <SuccessMessage imageSrc={PartyIcon.src} text={progressBarLanguage} />
      ) : null}

      {!!bucketsRemainingCount.length &&
      !prioritizeMinGameSelectionCount &&
      bucketsRemainingCount.every((bucket) => bucket.isComplete) ? (
        <SuccessMessage imageSrc={PartyIcon.src} text={progressBarLanguage} />
      ) : null}
    </Box>
  );
}

export function PackBucketsProgressBar() {
  const theme = useTheme();
  const { sm: smDown } = useCssMediaQuery("down");
  const {
    selectedPack,
    packBucketMin,
    packBucketMessage,
    packBucketsRemainingCount,
    selectedEventsPerPackBucket,
    getProgressBarSuccessLanguage,
  } = usePacks();

  const selectedEvents = useAppStore(selectEventsSelected);

  const progressBarSuccessLanguage = getProgressBarSuccessLanguage();

  const packBucketMinAdjusted = selectedPack?.priritizePackAmountForEventCount
    ? selectedPack?.amount || 0
    : packBucketMin;

  const isProgressNotReached =
    (selectedEventsPerPackBucket / packBucketMinAdjusted) * 100 != 100;

  const remaining = selectedEvents.length < (selectedPack?.amount || 0);

  return (
    <Box
      p={smDown ? 1 : 2}
      borderRadius={"8px"}
      border={`1px solid ${lighten(theme.palette.primary.main, 0.6)}`}
      bgcolor={lighten(theme.palette.primary.main, 0.95)}
      width={smDown ? "100%" : "400px"}
    >
      {/* Progress bar */}
      {isProgressNotReached &&
      !selectedPack?.priritizePackAmountForEventCount ? (
        <Box width="100%">
          <Box>
            <LinearProgressBar
              progress={(selectedEventsPerPackBucket / packBucketMin) * 100}
            />
          </Box>

          {/* progress message */}
          {packBucketMessage ? (
            <Box
              mt={1}
              maxWidth={"100%"}
              sx={{
                [theme.breakpoints.down(421)]: {
                  maxWidth: 160,
                },
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight={"500"}
                fontSize={smDown ? "12px" : "14px"}
              >
                {packBucketMessage}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {selectedPack?.priritizePackAmountForEventCount && remaining ? (
        <Box width="100%">
          <Box>
            <LinearProgressBar
              progress={(selectedEvents.length / selectedPack.amount) * 100}
            />
          </Box>

          {/* progress message */}
          {packBucketMessage ? (
            <Box
              mt={1}
              maxWidth={"100%"}
              sx={{
                [theme.breakpoints.down(421)]: {
                  maxWidth: 160,
                },
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight={"500"}
                fontSize={smDown ? "12px" : "14px"}
              >
                {packBucketMessage}
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* success language */}
      {!selectedPack?.priritizePackAmountForEventCount &&
      !!packBucketsRemainingCount.length &&
      packBucketsRemainingCount.every((bucket) => bucket.isComplete) ? (
        <SuccessMessage
          imageSrc={PartyIcon.src}
          text={progressBarSuccessLanguage}
        />
      ) : null}

      {selectedPack?.priritizePackAmountForEventCount && !remaining ? (
        <SuccessMessage
          imageSrc={PartyIcon.src}
          text={progressBarSuccessLanguage}
        />
      ) : null}
    </Box>
  );
}
